// src/components/SimpleComponents/SimpleNumber.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Input, Select, Space } from 'antd';
import { useSimpleFormContext } from './SimpleFormProvider';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import useCustomValidationStatus from 'hooks/useCustomValidationStatus';

const { Option } = Select;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const LOCALE_CONFIGS = {
  'pt-BR': { thousandSeparator: '.', decimalSeparator: ',' },
  'en-US': { thousandSeparator: ',', decimalSeparator: '.' },
  default: { thousandSeparator: ',', decimalSeparator: '.' },
};

const ADDON_STYLE_BASE = { padding: '0 11px', display: 'flex', alignItems: 'center', border: '1px solid var(--ant-color-border)', borderLeft: 'none', borderRadius: '0 6px 6px 0' };
const INPUT_STYLE_BASE = { height: 40, textAlign: 'right' };

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleNumber = ({
  name,
  style = {},
  subdocumentField,

  // Locale and formatting
  locale = 'pt-BR',
  showThousandSeparator = true,
  showDecimalSeparator = true,
  customThousandSeparator,
  customDecimalSeparator,

  // Number config
  decimalPlaces = 0,
  min,
  max,
  rounding = 'natural', // 'up' | 'down' | 'natural'

  // Percentage suffix
  percentage = false,
  'x-percentage-style': xPercentageStyle = { minWidth: 40 },

  // Suffix (component or select options)
  suffix,
  suffixDefaultValue,
  suffixValue,
  onSuffixChange,
  showSuffixSelect = true,
  'x-suffix-style': xSuffixStyle = { minWidth: 40 },

  // Prefix (component or select options)
  prefix,
  prefixDefaultValue,
  onPrefixChange,
  'x-prefix-style': xPrefixStyle = {},

  // Other props
  disabled,
  onBlur,
  onChange,
  placeholder,

  ...props
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { locked, disabledFields, form } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;
  const { customValidationStatus, customValidationMessage } =
    useCustomValidationStatus({ ...props, ...elementProps });

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);
  const localeConfig = LOCALE_CONFIGS[locale] || LOCALE_CONFIGS.default;

  const thousandSep =
    customThousandSeparator !== undefined
      ? customThousandSeparator
      : showThousandSeparator
        ? localeConfig.thousandSeparator
        : '';

  const decimalSep =
    customDecimalSeparator !== undefined
      ? customDecimalSeparator
      : showDecimalSeparator && decimalPlaces > 0
        ? localeConfig.decimalSeparator
        : '';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [displayValue, setDisplayValue] = useState('');
  const [selectedSuffix, setSelectedSuffix] = useState(
    suffixDefaultValue || suffix?.[0]?.value
  );
  const [selectedPrefix, setSelectedPrefix] = useState(prefixDefaultValue);
  const isFocusedRef = useRef(false);

  // Watch form value changes using Form.useWatch
  const watchedValue = Form.useWatch(fullFieldName, form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  // Sync controlled suffixValue → internal selectedSuffix
  useEffect(() => {
    if (suffixValue !== undefined && suffixValue !== selectedSuffix) {
      setSelectedSuffix(suffixValue);
    }
  }, [suffixValue]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync display value with form value when it changes externally (not while user is editing)
  useEffect(() => {
    // Don't update display while user is focused/editing
    if (isFocusedRef.current) return;

    if (watchedValue !== undefined && watchedValue !== null && !isNaN(watchedValue)) {
      const formattedValue = formatNumber(watchedValue, {
        thousandSep,
        decimalSep,
        decimalPlaces,
      });
      setDisplayValue(formattedValue);
    } else if (watchedValue === undefined || watchedValue === null) {
      setDisplayValue('');
    }
  }, [watchedValue, thousandSep, decimalSep, decimalPlaces]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleInputChange = useCallback(
    ev => {
      const rawValue = ev?.target?.value ?? '';

      // Allow typing: only digits, decimal separator, and minus sign
      const allowedChars = decimalPlaces > 0 ? `0-9${escapeRegex(decimalSep)}-` : '0-9-';
      const regex = new RegExp(`^[${allowedChars}]*$`);
      if (!regex.test(rawValue) && rawValue !== '') return;

      // Prevent multiple decimal separators
      if (decimalSep && decimalPlaces > 0) {
        const sepCount = (rawValue.match(new RegExp(escapeRegex(decimalSep), 'g')) || []).length;
        if (sepCount > 1) return;
      }

      // Prevent multiple minus signs or minus not at start
      const minusCount = (rawValue.match(/-/g) || []).length;
      if (minusCount > 1 || (minusCount === 1 && rawValue.indexOf('-') !== 0)) return;

      // Update display value while typing (raw, unformatted)
      setDisplayValue(rawValue);

      // Parse and set form value
      const numericValue = parseLocaleNumber(rawValue, decimalSep);

      if (!isNaN(numericValue) && rawValue !== '' && rawValue !== '-') {
        form?.setFieldValue(fullFieldName, numericValue);
        onChange?.(numericValue);
      } else if (rawValue === '' || rawValue === '-') {
        form?.setFieldValue(fullFieldName, null);
        onChange?.(null);
      }
    },
    [decimalSep, decimalPlaces, form, fullFieldName, onChange]
  );

  const handleBlur = useCallback(
    ev => {
      isFocusedRef.current = false;
      const rawValue = ev?.target?.value ?? '';

      if (rawValue === '' || rawValue === '-') {
        setDisplayValue('');
        onBlur?.(ev);
        return;
      }

      // Parse current value
      const cleanValue = rawValue.replace(new RegExp(`[${escapeRegex(thousandSep)}]`, 'g'), '');
      let numericValue = parseLocaleNumber(cleanValue, decimalSep);

      if (isNaN(numericValue)) {
        setDisplayValue('');
        form?.setFieldValue(fullFieldName, null);
        onBlur?.(ev);
        return;
      }

      // Apply min/max
      if (min !== undefined && numericValue < min) numericValue = min;
      if (max !== undefined && numericValue > max) numericValue = max;

      // Apply rounding
      numericValue = applyRounding(numericValue, decimalPlaces, rounding);

      // Format final value
      const formattedValue = formatNumber(numericValue, {
        thousandSep,
        decimalSep,
        decimalPlaces,
      });

      setDisplayValue(formattedValue);
      form?.setFieldValue(fullFieldName, numericValue);

      onBlur?.(ev);
    },
    [thousandSep, decimalSep, decimalPlaces, rounding, min, max, form, fullFieldName, onBlur]
  );

  const handleFocus = useCallback(
    ev => {
      isFocusedRef.current = true;

      // Remove thousand separators for easier editing
      const currentValue = ev?.target?.value ?? '';
      if (thousandSep && currentValue.includes(thousandSep)) {
        const cleanValue = currentValue.replace(new RegExp(escapeRegex(thousandSep), 'g'), '');
        setDisplayValue(cleanValue);
      }

      // Select all text on focus for easy editing
      setTimeout(() => {
        ev?.target?.select?.();
      }, 0);
    },
    [thousandSep]
  );

  const handleSuffixChange = useCallback(
    value => {
      setSelectedSuffix(value);
      onSuffixChange?.(value);
    },
    [onSuffixChange]
  );

  const handlePrefixChange = useCallback(
    value => {
      setSelectedPrefix(value);
      onPrefixChange?.(value);
    },
    [onPrefixChange]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const hasPrefix = !!prefix;
  const hasSuffix = (showSuffixSelect && suffix?.length > 0) || percentage;
  const needsCompact = hasPrefix || hasSuffix;

  // Build prefix element
  const prefixElement = useMemo(() => {
    if (!prefix) return null;

    // If prefix is array of options, render Select
    if (Array.isArray(prefix) && typeof prefix[0] === 'object') {
      return (
        <Select
          disabled={isDisabled}
          value={selectedPrefix}
          defaultValue={prefixDefaultValue}
          onChange={handlePrefixChange}
          style={{ minWidth: 80, ...xPrefixStyle }}
          getPopupContainer={trigger => trigger.parentNode}
        >
          {prefix.map(opt => (
            <Option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label ?? opt.text ?? opt.value}
            </Option>
          ))}
        </Select>
      );
    }

    // If prefix is a React element or string, return as-is
    return prefix;
  }, [prefix, isDisabled, selectedPrefix, prefixDefaultValue, handlePrefixChange, xPrefixStyle]);

  // Build suffix element
  const suffixElement = useMemo(() => {
    // Percentage suffix takes precedence
    if (percentage) {
      return (
        <span className="ant-input-group-addon" style={{ ...ADDON_STYLE_BASE, ...xPercentageStyle }}>
          %
        </span>
      );
    }

    if (!showSuffixSelect || !suffix?.length) return null;

    if (suffix.length === 1) {
      // Single suffix - show as text (using Ant Design Input addon style)
      return (
        <span className="ant-input-group-addon" style={{ ...ADDON_STYLE_BASE, ...xSuffixStyle }}>
          {suffix[0].label}
        </span>
      );
    }

    // Multiple suffixes - show Select (wider to accommodate dropdown arrow)
    return (
      <Select
        disabled={isDisabled}
        value={selectedSuffix}
        defaultValue={suffixDefaultValue || suffix[0]?.value}
        onChange={handleSuffixChange}
        style={{ width: 70, minWidth: 70, ...xSuffixStyle }}
        getPopupContainer={trigger => trigger.parentNode}
      >
        {suffix.map(opt => (
          <Option key={opt.value} value={opt.value} disabled={opt.disabled} title={opt.text}>
            {opt.label}
          </Option>
        ))}
      </Select>
    );
  }, [
    showSuffixSelect,
    suffix,
    percentage,
    isDisabled,
    selectedSuffix,
    suffixDefaultValue,
    handleSuffixChange,
    xSuffixStyle,
    xPercentageStyle,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  const inputElement = (
    <Input
      ref={elementRef}
      id={flattenFieldName}
      style={{ ...INPUT_STYLE_BASE, width: needsCompact ? undefined : '100%', flex: needsCompact ? 1 : undefined, ...style }}
      value={displayValue}
      onChange={handleInputChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      disabled={isDisabled}
      placeholder={placeholder ?? (decimalPlaces > 0 ? '0' + decimalSep + '0'.repeat(decimalPlaces) : '0')}
    />
  );

  return (
    <SimpleFormItem
      {...props}
      name={fullFieldName}
      elementProps={elementProps}
      customValidationStatus={customValidationStatus}
      customValidationMessage={customValidationMessage}
    >
      {needsCompact ? (
        <Space.Compact style={{ width: '100%' }}>
          {prefixElement}
          {inputElement}
          {suffixElement}
        </Space.Compact>
      ) : (
        inputElement
      )}
    </SimpleFormItem>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleNumber;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function escapeRegex(str) {
  if (!str) return '';
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function parseLocaleNumber(value, decimalSep) {
  if (!value || value === '') return NaN;

  // Replace locale decimal separator with standard dot
  const normalized = decimalSep ? value.replace(decimalSep, '.') : value;
  return parseFloat(normalized);
}

function applyRounding(value, decimalPlaces, rounding) {
  if (isNaN(value)) return value;

  const multiplier = Math.pow(10, decimalPlaces);

  switch (rounding) {
    case 'up':
      return Math.ceil(value * multiplier) / multiplier;
    case 'down':
      return Math.floor(value * multiplier) / multiplier;
    case 'natural':
    default:
      return Math.round(value * multiplier) / multiplier;
  }
}

function formatNumber(value, { thousandSep, decimalSep, decimalPlaces }) {
  if (value === null || value === undefined) return '';

  // Ensure value is a number
  const numValue = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(numValue)) return '';

  // Format with fixed decimal places
  const parts = numValue.toFixed(decimalPlaces).split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1] || '';

  // Add thousand separators
  let formattedInteger = integerPart;
  if (thousandSep) {
    // Handle negative numbers
    const isNegative = formattedInteger.startsWith('-');
    const absInteger = isNegative ? formattedInteger.slice(1) : formattedInteger;
    formattedInteger = absInteger.replace(/\B(?=(\d{3})+(?!\d))/g, thousandSep);
    if (isNegative) formattedInteger = '-' + formattedInteger;
  }

  // Combine with decimal separator
  if (decimalPlaces > 0 && decimalSep) {
    return `${formattedInteger}${decimalSep}${decimalPart}`;
  }

  return formattedInteger;
}
